"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.emitProjectModel = emitProjectModel;
const constants_1 = require("../style/constants");
const layers_1 = require("./layers");
function emitProjectModel(blocks, layout, opts) {
    const blockById = new Map(blocks.map((b) => [b.id, b]));
    const drawings = {};
    const addDrawing = (layer, drawing) => {
        (drawings[layer] = drawings[layer] ?? []).push(drawing);
    };
    addDrawing(layers_1.BlockDiagramLayerId.BoardOutline, {
        type: 'rect',
        start: [layout.boardOutline.x, layout.boardOutline.y],
        end: [layout.boardOutline.x + layout.boardOutline.w, layout.boardOutline.y + layout.boardOutline.h],
        strokeWidth: constants_1.BOARD_OUTLINE_STROKE_W,
    });
    addDrawing(layers_1.BlockDiagramLayerId.BoardLabel, {
        type: 'text',
        value: 'BOARD',
        pos: [layout.boardOutline.x + 14, layout.boardOutline.y + 10],
        size: constants_1.BOARD_LABEL_FONT_SIZE,
        justify: [-1, 0],
        thickness: 1,
    });
    for (const edge of layout.edges) {
        const layer = (0, layers_1.edgeLayerId)(edge.kind, edge.voltageRank);
        const style = constants_1.EDGE_STYLE[edge.kind];
        const net = edge.nets[0];
        for (let i = 1; i < edge.points.length; i++) {
            const [x1, y1] = edge.points[i - 1];
            const [x2, y2] = edge.points[i];
            if (x1 === x2 && y1 === y2) {
                continue;
            }
            addDrawing(layer, {
                type: 'segment',
                start: [x1, y1],
                end: [x2, y2],
                width: style.width,
                style: style.lineStyle,
                ...(net !== undefined ? { net } : {}),
                metadata: { blockDiagramEdgeKey: edge.key },
            });
        }
        if (opts.showArrows) {
            for (const arrow of arrowheads(edge)) {
                addDrawing(layer, { ...arrow, ...(net !== undefined ? { net } : {}) });
            }
        }
    }
    for (const chip of layout.chips) {
        const w = chip.text.length * constants_1.CHIP_CHAR_W + constants_1.CHIP_PAD_W;
        addDrawing(layers_1.BlockDiagramLayerId.EdgeLabels, {
            type: 'rect',
            start: [chip.x - w / 2, chip.y - constants_1.CHIP_H / 2],
            end: [chip.x + w / 2, chip.y + constants_1.CHIP_H / 2],
            strokeWidth: 0,
            filled: true,
            metadata: { blockDiagramEdgeKey: chip.key },
        });
        addDrawing(layers_1.BlockDiagramLayerId.EdgeLabels, {
            type: 'text',
            value: chip.text,
            pos: [chip.x, chip.y],
            size: constants_1.CHIP_FONT_SIZE,
            justify: [0, 0],
            thickness: 1,
            contrastWithLayer: true,
            metadata: { blockDiagramEdgeKey: chip.key },
        });
    }
    const components = {};
    const modules = [];
    const projectModules = [];
    for (const node of layout.nodes) {
        const block = blockById.get(node.id);
        if (block === undefined) {
            continue;
        }
        const componentRef = `bd_cmp_${node.id}`;
        const footprintRef = `bd_fp_${node.id}`;
        const layers = (0, layers_1.blockLayerIds)(node.kind);
        const strokeW = node.kind === 'processor' ? constants_1.PROCESSOR_STROKE_W : constants_1.BLOCK_STROKE_W;
        const nameSize = node.big ? constants_1.NAME_FONT_SIZE_BIG : constants_1.NAME_FONT_SIZE;
        const subSize = node.big ? constants_1.SUB_FONT_SIZE_BIG : constants_1.SUB_FONT_SIZE;
        const maxLabelW = node.w - 2 * constants_1.BLOCK_LABEL_PAD_X;
        const name = (0, constants_1.truncateTextToWidth)(block.name, nameSize, maxLabelW);
        const sub = block.sub !== undefined ? (0, constants_1.truncateTextToWidth)(block.sub, subSize, maxLabelW) : undefined;
        const half = [node.w / 2, node.h / 2];
        const symbolDrawings = [
            {
                layers: [layers.fill],
                drawing: {
                    type: 'rect',
                    start: [-half[0], -half[1]],
                    end: [half[0], half[1]],
                    strokeWidth: 0,
                    filled: true,
                },
            },
            {
                layers: [layers.outline],
                drawing: {
                    type: 'rect',
                    start: [-half[0], -half[1]],
                    end: [half[0], half[1]],
                    strokeWidth: strokeW,
                },
            },
            {
                layers: [layers_1.BlockDiagramLayerId.BlockName],
                drawing: {
                    type: 'text',
                    value: name,
                    pos: [0, sub !== undefined ? -9 : 0],
                    size: nameSize,
                    justify: [0, 0],
                    thickness: 1.5,
                },
            },
        ];
        if (sub !== undefined) {
            symbolDrawings.push({
                layers: [layers_1.BlockDiagramLayerId.BlockSub],
                drawing: {
                    type: 'text',
                    value: sub,
                    pos: [0, 9],
                    size: subSize,
                    justify: [0, 0],
                    thickness: 1,
                },
            });
        }
        components[componentRef] = {
            name: componentRef,
            numPins: 0,
            numPads: 0,
            footprints: [],
            symbols: [
                {
                    ref: footprintRef,
                    name: footprintRef,
                    drawings: symbolDrawings,
                    pins: [],
                },
            ],
            signals: [],
            isVirtual: true,
        };
        modules.push({
            ref: node.id,
            footprintRef,
            componentRef,
            center: [node.x + half[0], node.y + half[1]],
            layer: 'F',
            transform: { angle: 0, mirrored: [false, false] },
            name: node.id,
            pinNets: {},
            drawProperties: {},
            customTypeName: (0, layers_1.blockCustomTypeName)(node.kind),
        });
        projectModules.push({
            name: node.id,
            instances: [{ ref: node.id, designId: layers_1.BLOCK_DIAGRAM_DESIGN_ID }],
            componentRef,
            value: block.sub,
            hideInBom: true,
        });
    }
    const nets = {};
    for (const edge of layout.edges) {
        for (const net of edge.nets) {
            nets[net] = nets[net] ?? { name: net, connections: [] };
        }
    }
    const bbox = { minx: 0, miny: 0, maxx: layout.canvas.width, maxy: layout.canvas.height };
    const design = {
        modelType: 'schematic',
        designId: layers_1.BLOCK_DIAGRAM_DESIGN_ID,
        designName: opts.title,
        sourceFile: '',
        drawings,
        edges_bbox: bbox,
        initial_zoombox: bbox,
        layerSet: layers_1.BLOCK_DIAGRAM_LAYER_SET_ID,
        warnings: [],
        nets,
        sheetIndex: 0,
        modules,
        ports: {},
    };
    return {
        type: 'cadstrom',
        name: opts.title,
        layerSets: { [layers_1.BLOCK_DIAGRAM_LAYER_SET_ID]: (0, layers_1.buildBlockDiagramLayers)(opts.showEdgeLabels) },
        designs: { [layers_1.BLOCK_DIAGRAM_DESIGN_ID]: design },
        components,
        modules: projectModules,
        warnings: [],
        processingResults: { type: 'project-processing', message: '', innerResults: [] },
        globalNets: {},
    };
}
function arrowheads(edge) {
    const out = [];
    const pts = edge.points;
    if (pts.length < 2) {
        return out;
    }
    if (edge.arrowEnd) {
        out.push(arrowAt(pts[pts.length - 2], pts[pts.length - 1]));
    }
    if (edge.arrowStart) {
        out.push(arrowAt(pts[1], pts[0]));
    }
    return out;
}
function arrowAt(from, tip) {
    const dx = tip[0] - from[0];
    const dy = tip[1] - from[1];
    const len = Math.hypot(dx, dy);
    const ux = len === 0 ? 1 : dx / len;
    const uy = len === 0 ? 0 : dy / len;
    const bx = tip[0] - ux * constants_1.ARROW_LEN;
    const by = tip[1] - uy * constants_1.ARROW_LEN;
    return {
        type: 'polygon',
        filled: true,
        width: 0,
        points: [
            [tip[0], tip[1]],
            [bx - uy * constants_1.ARROW_HALF_W, by + ux * constants_1.ARROW_HALF_W],
            [bx + uy * constants_1.ARROW_HALF_W, by - ux * constants_1.ARROW_HALF_W],
        ],
    };
}
//# sourceMappingURL=emit-project-model.js.map